import React, { createContext, useContext, useState } from 'react';
import { getCmEndpoint } from '../utils';
import localeService from '../utils/locale-service';
import { captureError } from '../utils/sentry';
import { SchedulingMode } from '../utils/zoomConfig';

type User = {
  displayName?: string;
  photoURL?: string;
  determinedUserIdentity?: string;
}

type Call = {
  destination?: string;
  dateAndTime?: string;
  agentName?: string;
  schedulingMode?: SchedulingMode;
}

export interface StateContextType {
  error: Error | null;
  setError(error: Error | null): void;
  getTravelRequest(id: string): Promise<{ customerFullName: string, destination?: string, agentName?: string, dateAndTime?: string, schedulingMode?: SchedulingMode, leadUuid?: string }>;
  user?: User | null;
  call?: Call | null;
  setUser(user: User): void;
  setCall(call: Call): void;
  signIn?(passcode?: string): Promise<void>;
  signOut?(): Promise<void>;
  isAuthReady?: boolean;
  isFetching: boolean;
  locale: string;
  setLocale(locale: string): void;
}

export const StateContext = createContext<StateContextType>(null!);

// Ride out a couple of transient network blips before surfacing a fatal error.
const MAX_FETCH_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [300, 600];

// A gap here would silently pass `undefined` to setTimeout below, which clamps to 0ms —
// turning "retry with backoff" into "retry immediately" with no signal that anything's wrong.
if (RETRY_DELAYS_MS.length !== MAX_FETCH_ATTEMPTS - 1) {
  throw new Error('RETRY_DELAYS_MS must have exactly MAX_FETCH_ATTEMPTS - 1 entries');
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// CM's lead_info endpoint never returns 404 (it looks up by find_by, not find) and is a
// cached, side-effect-free read, so retrying blindly on any non-2xx or network failure is
// cheap even for the cases where it turns out not to help (e.g. a deterministic 4xx) — not
// worth the added complexity of special-casing by status code.
async function attemptFetch(endpoint: string): Promise<Response> {
  const response = await fetch(endpoint, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Travel request fetch failed with status ${response.status}`);
  }
  return response;
}

export default function AppStateProvider(props: React.PropsWithChildren<{}>) {
  const [error, setError] = useState<Error | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [user, setUser] = useState<User>();
  const [call, setCall] = useState<Call>();
  const [locale, setLocale] = useState(localeService.getDefaultLocale());

  const contextValue = {
    error,
    setError,
    isFetching,
    user,
    setUser,
    call,
    setCall,
    locale,
    setLocale,
  } as StateContextType;

  const getTravelRequest: StateContextType['getTravelRequest'] = async (id) => {
    setIsFetching(true);
    let lastAttempt = 0;
    try {
      const endpoint = `${getCmEndpoint()}/video/lead_info/${encodeURIComponent(id)}`;
      let response: Response | undefined;
      for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt++) {
        lastAttempt = attempt;
        try {
          response = await attemptFetch(endpoint);
          break;
        } catch (err) {
          if (attempt === MAX_FETCH_ATTEMPTS) throw err;
          await delay(RETRY_DELAYS_MS[attempt - 1]);
        }
      }
      // Only reached for a 2xx response — a body that fails to parse here is terminal, not retried.
      return await response!.json();
    } catch (err) {
      captureError(err, 'travel_request_fetch', { travel_request_id: id, retry_count: String(lastAttempt) });
      setError(err as Error);
      throw err;
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <StateContext.Provider value={{ ...contextValue, getTravelRequest }}>
      {props.children}
    </StateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the AppStateProvider');
  }
  return context;
}
